import { Inject, Injectable, Optional } from "@angular/core";
import { mergeMap as _observableMergeMap, catchError as _observableCatch } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";

import { API_BASE_URL } from "./service-shared";
import { environment } from "environments/environment";
import { blobToText, processProblemResponse } from "./service-functions";
import { GetControlOptionsResponse, GetExternalDataResponse, SearchCloudDataResponse } from "./model/external-data-service.model";
import { DataContainer } from "../model";

@Injectable({ providedIn: 'root' })
export class ExternalDataServiceProxy
{
    private http: HttpClient;
    private baseUrl: string;
    private basePath: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string)
    {
        this.http = http;
        this.basePath = environment.config?.dataProxyEndpoint !== undefined ? environment.config.dataProxyEndpoint : '';
        this.baseUrl = (baseUrl !== undefined && baseUrl !== null ? baseUrl : "") + this.basePath;

    }

    getPaymentDefaults(formId: string, applicationId: string, paymentProviderKey: string): Observable<string>
    {
        let url_ = this.baseUrl + "/GetPaymentDefaults";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            body: { formId: formId, applicationId: applicationId, paymentProviderKey: paymentProviderKey},
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain",

            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetPaymentDefaults(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetPaymentDefaults(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<string>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<string>;
        }));
    }

    protected processGetPaymentDefaults(response: HttpResponseBase): Observable<string> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                return _observableOf(resultData200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
                ?? _observableOf<string>(null as any);
    }

    getExternalData(formId: string, formVersion: number | undefined, pageId: number, applicationId: string, applicationData: string): Observable<GetExternalDataResponse>
    {
        let url_ = this.baseUrl + "/GetExternalData";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            body: { formId: formId, formVersion: formVersion, pageId: pageId, applicationId: applicationId, applicationData: applicationData},
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetExternalData(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetExternalData(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<GetExternalDataResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<GetExternalDataResponse>;
        }));
    }

    protected processGetExternalData(response: HttpResponseBase): Observable<GetExternalDataResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                return _observableOf(resultData200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
                ?? _observableOf<string>(null as any);
    }

    searchExternalData(formId: string, formVersion: number | undefined, controlName: string, searchTerm: string, applicationId: string, applicationData: string): Observable<GetExternalDataResponse>
    {
        let url_ = this.baseUrl + "/SearchExternalData";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            body: { formId: formId, formVersion: formVersion, controlName: controlName, searchTerm: searchTerm, applicationId: applicationId, applicationData: applicationData},
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain",
            }),
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSearchExternalData(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSearchExternalData(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e));
                }
            } else
                return _observableThrow(() => new Error(response_));
        }));
    }

    protected processSearchExternalData(response: HttpResponseBase): Observable<GetExternalDataResponse> {
        const status = response.status;

        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetExternalDataResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }
        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
                ?? _observableOf<GetExternalDataResponse>(null as any);
    }

    searchCloudData(formId: string, formVersion: number | undefined, controlName: string, searchTerm: string, category: string, pageId: number, applicationId: string, searchByValue: boolean): Observable<SearchCloudDataResponse>
    {
        let url_ = this.baseUrl + "/SearchCloudData";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            body: { formId: formId, formVersion: formVersion, controlName: controlName, searchTerm: searchTerm, category: category, pageId: pageId, applicationId: applicationId, searchByValue: searchByValue},
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSearchCloudData(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSearchCloudData(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<SearchCloudDataResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<SearchCloudDataResponse>;
        }));
    }

    protected processSearchCloudData(response: HttpResponseBase): Observable<SearchCloudDataResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = SearchCloudDataResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
                ?? _observableOf<string>(null as any);
    }

    getControlOptions(formId: string, formVersion: number | undefined, controlName: string, applicationData: DataContainer, applicationId: string): Observable<GetControlOptionsResponse>
    {
        let url_ = this.baseUrl + "/GetControlOptions";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            body: { formId: formId, formVersion: formVersion, controlName: controlName, applicationData: JSON.stringify(applicationData), applicationId: applicationId},
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetControlOptions(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetControlOptions(response_ as any);
                } catch (e) {
                    return _observableThrow(() => new Error(e)) as any as Observable<GetControlOptionsResponse>;
                }
            } else
                return _observableThrow(() => new Error(response_)) as any as Observable<GetControlOptionsResponse>;
        }));
    }

    protected processGetControlOptions(response: HttpResponseBase): Observable<GetControlOptionsResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
                let result200: any = null;
                let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                result200 = GetControlOptionsResponse.fromJS(resultData200);
                return _observableOf(result200);
            }));
        }

        return processProblemResponse(status, responseBlob, _headers, this.jsonParseReviver)
                ?? _observableOf<string>(null as any);
    }
}
